"use client"

import { useEffect, useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { getPreviewAnalyticsSummary, type AnalyticsSummary } from "@/services/analytics-dashboard-service"
import { TrendingUp, RefreshCw, BarChart3 } from "lucide-react"
import { toast } from "sonner"
import { AnalyticsStatsCards } from "./analytics-stats-cards"
import { AnalyticsTopLists } from "./analytics-top-lists"
import { useTranslation } from "react-i18next";

interface AnalyticsSummaryProps {
  previewId: string
  className?: string
  onRefresh?: () => void
}

export default function AnalyticsOverview({
  previewId,
  className,
  onRefresh,
}: AnalyticsSummaryProps) {
  const { t } = useTranslation("analytics")
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const fetchAnalytics = useCallback(async () => {
    try {
      setError(null)
      const data = await getPreviewAnalyticsSummary(previewId)
      setAnalytics(data)
    } catch (err) {
      console.error("Error fetching analytics:", err)
      setError(err instanceof Error ? err.message : "Failed to load analytics")
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [previewId])

  useEffect(() => {
    if (previewId) {
      fetchAnalytics()
    }
  }, [previewId, fetchAnalytics])

  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    await fetchAnalytics()
    onRefresh?.()
    toast.success(t("analytics:analyticsTable.toast.refresh"));
  }, [fetchAnalytics, onRefresh])

  if (loading) {
    return (
      <div className={className}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              {t("analytics:analyticsOverview.title")}
            </CardTitle>
            <CardDescription>
              {t("analytics:analyticsOverview.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Stats Cards Skeleton */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-lg" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-5 w-12" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Top Lists Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Array.from({ length: 2 }).map((_, i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-5 w-32" />
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Array.from({ length: 3 }).map((_, j) => (
                      <div
                        key={j}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-4 w-6" />
                          <Skeleton className="h-4 w-20" />
                        </div>
                        <Skeleton className="h-5 w-8" />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error || !analytics) {
    return (
      <div className={className}>
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <BarChart3 className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="mb-2 text-lg font-semibold">
              {t("analytics:analyticsOverview.error.title")}
            </h3>
            <p className="text-muted-foreground mb-4 max-w-sm mx-auto">
              {error || t("analytics:analyticsOverview.error.description")}
            </p>
            <Button onClick={handleRefresh} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("analytics:analyticsOverview.error.retry")}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                {t("analytics:analyticsOverview.title")}
              </CardTitle>
              <CardDescription>
                {t("analytics:analyticsOverview.description")}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={refreshing}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
                />
                {t("analytics:analyticsOverview.refresh")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {analytics.total_views === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <BarChart3 className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mb-2 text-lg font-semibold">
                {t("analytics:analyticsOverview.empty.title")}
              </h3>
              <p className="text-muted-foreground max-w-sm mx-auto">
                {t("analytics:analyticsOverview.empty.description")}
              </p>
            </div>
          ) : (
            <>
              <AnalyticsStatsCards analytics={analytics} />
              <AnalyticsTopLists analytics={analytics} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
